import React, { useState } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import RadioButton from '@amzn/meridian/radio-button';
import Text from '@amzn/meridian/text';
import useCustomTracking from '../../../Util/useCustomTracking';
import ErrorMessage from './ErrorMessage';

type RadioButtonValueType = React.ComponentProps<typeof RadioButton>['value'];

interface RadioButtonOption extends React.ComponentProps<typeof RadioButton> {
  label: string;
}

interface RadioButtonGroupProps
  extends React.ComponentProps<typeof RadioButton> {
  options: RadioButtonOption[];
  selectedValue: RadioButtonValueType;
  errorText?: string;
  'data-*'?: string;
}

const RadioButtonGroupComponent: React.FC<RadioButtonGroupProps> = ({
  ...props
}: RadioButtonGroupProps): React.ReactElement => {
  const { errorText } = props;
  return (
    <div>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <BasicRadioButtonGroup {...props} />
      {errorText && <ErrorMessage message={errorText} />}
    </div>
  );
};

const BasicRadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
  name,
  selectedValue,
  onChange,
  disabled,
  options,
  ...props
}: RadioButtonGroupProps): React.ReactElement => {
  const [selected, setSelected] = useState(selectedValue);
  const [RadioButtonGroupTrackingTag, radioButtonClick] = useCustomTracking(
    `coaching-form-radio-button-group-field-${name}`,
    `coaching-form-radio-button-group-field-${name}-slot`,
    {
      'data-csa-c-type': 'radio-button',
      'data-csa-c-selected-value': selected || '',
    }
  );

  const handleChange = (a: RadioButtonValueType): void => {
    setSelected(a);
    if (onChange) onChange({ target: { name, value: a } });
    radioButtonClick();
  };

  const radioButtonOptions = options.map((option: RadioButtonOption) => (
    <RadioButton
      value={option.value}
      name={option.label}
      key={`${name}_value_${option.value}`}
      disabled={option.disabled ?? false}
      checked={option.value === selected}
      onChange={handleChange}
    >
      {option.label}
    </RadioButton>
  ));

  return (
    <>
      <RadioButtonGroupTrackingTag />
      <Column spacingInset='300 none' spacing='400'>
        <Text>
          Was this is an accurate recommendation based on the category?
        </Text>
        <Row alignmentHorizontal='start' {...props}>
          {radioButtonOptions}
        </Row>
      </Column>
    </>
  );
};

export default RadioButtonGroupComponent;
